//@flow
import * as React from 'react';
import { translate } from 'react-i18next';
import classnames from 'classnames';
import * as Immutable from 'immutable';

import { memoized } from 'common/utils/Memoizer';
import {
    Button,
    ButtonWithPopover,
    ButtonTypes
} from 'common/components/core/button';
import {
    PopoverPlaceEnum,
    PopoverTriggerEnum
} from 'common/components/core/popover/popOverFactory';
import { ZoneMapGeometryTypeEnum } from 'common/enums/ZoneMapGeometryType';
import { inputWithRendererFactory } from 'common/components/core/input/InputWithRenderer';
import { EnhancedField } from 'common/components/core/form/Field';
import EditableChipsListField from 'common/components/common/EditableChipsListField';
import RiderEditableChips from 'common/components/zoneMap/RiderEditableChips';
import VehicleEditableChips from 'common/components/zoneMap/VehicleEditableChips';
import { VehicleModelEditableChips } from 'common/components/zoneMap/VehicleModelEditableChips';
import Switch from 'common/components/core/switch';

import { zoneMapColors } from 'common/enums/ColorEnum';
import ColourSelector from './ColorSelector';
import FolderSelector from './FolderSelector';

import Icon from 'common/components/core/icon';
import StringUtils from 'common/utils/StringUtils';

import theme from './ZoneDetailsPopupView.scss';

import type { TFunction } from 'react-i18next';
import type { MapMetadataSettingsType } from 'common/models/flow/EntitySettings';
import type { UserId } from 'common/models/internal/UserId';
import type { ExtendedUser } from 'common/models/internal/ExtendedUser';
import type { MasterVehicle } from 'common/models/internal/MasterVehicle';
import type { VehicleModel } from 'common/models/internal/plan';
import type { VehicleId } from 'common/models/internal/VehicleId';
import type { VehicleModelId } from 'common/models/internal/VehicleModelId';
import type { List } from 'immutable';

const EnhancedInput = inputWithRendererFactory({});

const ZoneDetailsPopupHeader = ({
    t,
    isEditable,
    name,
    onNameChange,
    disableMapEvents,
    enableMapEvents,
    routeId,
    onRouteIdChange,
    onClose,
    zoneType
}) => (
    <div className={theme.headerWrap}>
        <div className={theme.title}>
            <div className={theme.name}>
                {isEditable ? (
                    <div className={theme.label}>
                        {zoneType === ZoneMapGeometryTypeEnum.Polyline
                            ? t('zoneMap.lineName')
                            : t('zoneMap.zoneName')}
                    </div>
                ) : null}
                <EnhancedField
                    type="text"
                    element="input"
                    value={
                        !isEditable && routeId ? `${name} - ${routeId}` : name
                    }
                    placeholder={
                        zoneType === ZoneMapGeometryTypeEnum.Polyline
                            ? t('zoneMap.untitledLine')
                            : t('zoneMap.untitledZone')
                    }
                    disabled={!isEditable}
                    theme={theme}
                    className={classnames(theme.zoneName, theme.formInput)}
                    onChange={onNameChange}
                    onMouseEnter={disableMapEvents}
                    onMouseLeave={enableMapEvents}
                />
            </div>

            {isEditable ? (
                <div className={theme.routeIdWrap}>
                    <div className={theme.label}>
                        {zoneType === ZoneMapGeometryTypeEnum.Polyline
                            ? t('zoneMap.lineId')
                            : t('zoneMap.routeId')}
                    </div>
                    <EnhancedField
                        type="text"
                        element="input"
                        value={routeId}
                        theme={theme}
                        className={theme.formInput}
                        onChange={onRouteIdChange}
                        onMouseEnter={disableMapEvents}
                        onMouseLeave={enableMapEvents}
                    />
                </div>
            ) : null}
        </div>

        <ButtonWithPopover
            theme={theme}
            buttonType={ButtonTypes.Icon}
            className={theme.closeBtn}
            icon="cancel"
            onClick={onClose}
        />
    </div>
);

type ContentProps = {
    t: TFunction,
    isEditable: boolean,
    localities: List<string>,
    riders: List<UserId>,
    availableRiders: List<ExtendedUser>,
    onRidersChange: () => void,
    onLocalitiesChange: () => void,
    disableMapEvents: () => void,
    enableMapEvents: () => void,
    zonePincodes: List<string>,
    onPincodesChange: () => void,
    description: string,
    onDescriptionChange: () => void,
    zoneType: string,
    vehicles: List<VehicleId>,
    vehicleModels: List<VehicleModelId>,
    availableVehicles: Array<MasterVehicle>,
    availableVehicleModels: List<VehicleModel>,
    onVehiclesChange: () => void,
    onVehicleModelsChange: () => void,
    mapMetadataSettings: MapMetadataSettingsType
};

type ContentState = {
    isFocused: boolean,
    showUserId: boolean,
    showVehicleId: boolean,
    showVehicleModelId: boolean
};

class ZoneDetailsPopupContent extends React.PureComponent<
    ContentProps,
    ContentState
> {
    state = {
        isFocused: false,
        showUserId: false,
        showVehicleId: false,
        showVehicleModelId: false
    };

    setActive = () => this.setState({ isFocused: true });

    setInactive = () => this.setState({ isFocused: false });

    handleShowUserIdSlider = value => this.setState({ showUserId: value });

    handleShowVehicleIdSlider = value =>
        this.setState({ showVehicleId: value });

    handleShowVehicleModelSlider = value =>
        this.setState({ showVehicleModelId: value });

    formatCreateLabel = label => this.props.t('zoneMap.createValue', { label });

    getOptions = values => {
        let options = [];
        if (values) {
            values.forEach(value => {
                if (value && !StringUtils.isEmpty(value.trim())) {
                    options = options.concat({
                        label: value,
                        value
                    });
                }
            });
        }
        return options;
    };

    @memoized()
    getActivatedRiders(availableRiders, riders) {
        return availableRiders.filter(availableRider =>
            riders.find(
                rider => availableRider.get('userId') === rider.get('userId')
            )
        );
    }

    @memoized()
    getActivatedVehicles(availableVehicles, vehicles) {
        return availableVehicles.filter(availableVehicle =>
            vehicles.find(
                vehicle => availableVehicle.get('id') === vehicle.get('id')
            )
        );
    }
    @memoized()
    getActivatedVehicleModels(
        availableVehicleModels: List<VehicleModel>,
        vehicleModels: List<VehicleModelId>
    ): List<VehicleModel> {
        return availableVehicleModels.filter(availableVehicle =>
            vehicleModels.find(
                vehicle => availableVehicle.id.modelId === vehicle.modelId
            )
        );
    }

    getLocalities = () => {
        const {
            localities,
            isEditable,
            zoneType,
            onLocalitiesChange,
            disableMapEvents,
            enableMapEvents,
            mapMetadataSettings,
            t
        } = this.props;
        if (!mapMetadataSettings.showPlaces) {
            return null;
        }
        const shouldRenderLocalities =
            (localities?.size || isEditable) &&
            zoneType !== ZoneMapGeometryTypeEnum.Polyline;
        return shouldRenderLocalities ? (
            <div className={classnames(theme.localities, theme.fieldInput)}>
                {localities?.size ? (
                    <div className={theme.label}>
                        {t('zoneMap.localitiesInZone')}
                    </div>
                ) : null}
                <EditableChipsListField
                    saveOnEnter={false}
                    noHover
                    emptyStateText={t('zoneMap.addLocalities')}
                    formatCreateLabel={this.formatCreateLabel}
                    disabled={!isEditable}
                    isFocused={isEditable}
                    theme={theme}
                    defaultFocused={false}
                    onMouseEnter={disableMapEvents}
                    onMouseLeave={enableMapEvents}
                    onFocus={disableMapEvents}
                    onBlur={enableMapEvents}
                    isEditingEnabled={isEditable}
                    value={localities.toJS()}
                    customFieldClass={
                        localities.size === 0 ? theme.isEmpty : null
                    }
                    onSave={onLocalitiesChange}
                    options={this.getOptions(localities)}
                />
            </div>
        ) : null;
    };

    getPincodes = () => {
        const {
            zonePincodes,
            isEditable,
            zoneType,
            onPincodesChange,
            disableMapEvents,
            enableMapEvents,
            mapMetadataSettings,
            t
        } = this.props;
        if (!mapMetadataSettings.showPincodes) {
            return null;
        }
        const shouldRenderPincodes =
            (zonePincodes?.size || isEditable) &&
            zoneType !== ZoneMapGeometryTypeEnum.Polyline;
        return shouldRenderPincodes ? (
            <div className={classnames(theme.pincodes, theme.fieldInput)}>
                {zonePincodes?.size ? (
                    <div className={theme.label}>
                        {t('zoneMap.pincodesInZone')}
                    </div>
                ) : null}
                <EditableChipsListField
                    saveOnEnter={false}
                    noHover
                    formatCreateLabel={this.formatCreateLabel}
                    emptyStateText={t('zoneMap.addPincodes')}
                    disabled={!isEditable}
                    isFocused={isEditable}
                    theme={theme}
                    defaultFocused={false}
                    onFocus={disableMapEvents}
                    onBlur={enableMapEvents}
                    onChange={onPincodesChange}
                    onMouseEnter={disableMapEvents}
                    onMouseLeave={enableMapEvents}
                    customFieldClass={
                        zonePincodes.size === 0 ? theme.isEmpty : null
                    }
                    isEditingEnabled={isEditable}
                    value={zonePincodes.toJS()}
                    onSave={onPincodesChange}
                    options={this.getOptions(zonePincodes)}
                />
            </div>
        ) : null;
    };

    getRiders = () => {
        const {
            riders,
            isEditable,
            onRidersChange,
            disableMapEvents,
            enableMapEvents,
            availableRiders,
            mapMetadataSettings,
            t
        } = this.props;
        if (!mapMetadataSettings.showRiders) {
            return null;
        }
        const { showUserId } = this.state;
        const activatedRiders = this.getActivatedRiders(
            availableRiders,
            riders
        );
        return riders?.size || isEditable ? (
            <div className={classnames(theme.riders, theme.fieldInput)}>
                {riders?.size ? (
                    <div className={theme.riderLabelWrap}>
                        <div className={theme.label}>
                            {`${t('entities:rider_plural')} (${
                                activatedRiders.size
                            })`}
                        </div>
                        <div className={theme.sliderWrap}>
                            <Switch
                                onChange={this.handleShowUserIdSlider}
                                checked={showUserId}
                                theme={theme}
                                label={t('zoneMap.showRiderId')}
                            />
                        </div>
                    </div>
                ) : null}
                <RiderEditableChips
                    emptyStateText={t('zoneMap.addRiders')}
                    theme={theme}
                    onRidersChange={onRidersChange}
                    isEditable={isEditable}
                    enableMapEvents={enableMapEvents}
                    riders={riders}
                    availableRiders={availableRiders}
                    disableMapEvents={disableMapEvents}
                    showUserId={showUserId}
                />
            </div>
        ) : null;
    };

    getVehicles = () => {
        const {
            vehicles,
            isEditable,
            onVehiclesChange,
            disableMapEvents,
            enableMapEvents,
            availableVehicles,
            mapMetadataSettings,
            t
        } = this.props;
        if (!mapMetadataSettings.showVehicles) {
            return null;
        }
        const { showVehicleId } = this.state;
        const getActivatedVehicles = this.getActivatedVehicles(
            availableVehicles,
            vehicles
        );
        return vehicles?.size || isEditable ? (
            <div className={classnames(theme.riders, theme.fieldInput)}>
                {vehicles?.size ? (
                    <div className={theme.riderLabelWrap}>
                        <div className={theme.label}>
                            {`${t('entities:vehicle_plural')} (${
                                getActivatedVehicles.size
                            })`}
                        </div>
                        <div className={theme.sliderWrap}>
                            <Switch
                                onChange={this.handleShowVehicleIdSlider}
                                checked={showVehicleId}
                                theme={theme}
                                label={t('zoneMap.showVehicleId')}
                            />
                        </div>
                    </div>
                ) : null}
                <VehicleEditableChips
                    emptyStateText={t('zoneMap.addVehicles')}
                    theme={theme}
                    onVehiclesChange={onVehiclesChange}
                    isEditable={isEditable}
                    enableMapEvents={enableMapEvents}
                    vehicles={vehicles || Immutable.List()}
                    availableVehicles={availableVehicles}
                    disableMapEvents={disableMapEvents}
                    showVehicleId={showVehicleId}
                />
            </div>
        ) : null;
    };

    getVehicleModels = () => {
        const {
            vehicleModels,
            isEditable,
            onVehicleModelsChange,
            disableMapEvents,
            enableMapEvents,
            availableVehicleModels,
            mapMetadataSettings,
            t
        } = this.props;
        if (!mapMetadataSettings.showVehicleModels) {
            return null;
        }
        const { showVehicleModelId } = this.state;
        const getActivatedVehicles = this.getActivatedVehicleModels(
            availableVehicleModels,
            vehicleModels
        );
        return !vehicleModels.isEmpty() || isEditable ? (
            <div className={classnames(theme.riders, theme.fieldInput)}>
                {!vehicleModels.isEmpty() ? (
                    <div className={theme.riderLabelWrap}>
                        <div className={theme.label}>
                            {`${t('entities:vehicleModel_plural')} (${
                                getActivatedVehicles.size
                            })`}
                        </div>
                        <div className={theme.sliderWrap}>
                            <Switch
                                onChange={this.handleShowVehicleModelSlider}
                                checked={showVehicleModelId}
                                theme={theme}
                                label={t('zoneMap.showVehicleModelId')}
                            />
                        </div>
                    </div>
                ) : null}
                <VehicleModelEditableChips
                    emptyStateText={t('zoneMap.addVehicleModels')}
                    theme={theme}
                    onVehicleModelsChange={onVehicleModelsChange}
                    isEditable={isEditable}
                    enableMapEvents={enableMapEvents}
                    vehicleModels={vehicleModels}
                    availableVehicleModels={availableVehicleModels}
                    disableMapEvents={disableMapEvents}
                    showVehicleId={showVehicleModelId}
                />
            </div>
        ) : null;
    };

    getDescription = () => {
        const {
            description,
            isEditable,
            onDescriptionChange,
            disableMapEvents,
            enableMapEvents,
            mapMetadataSettings,
            t
        } = this.props;
        if (!mapMetadataSettings.showDescription) {
            return null;
        }
        const { isFocused } = this.state;
        const descriptionLabel = (
            <div className={theme.addDescriptionWrap}>
                <Icon
                    theme={theme}
                    className={theme.addTagIcon}
                    icon={'plus-thin'}
                />
                <div className={theme.addDescriptionLabel}>
                    {t('zoneMap.addDescription')}
                </div>
            </div>
        );
        return description || isEditable ? (
            <div className={classnames(theme.description, theme.fieldInput)}>
                {description || isFocused ? (
                    <div className={theme.label}>
                        {t('zoneMap.description')}
                    </div>
                ) : null}
                {description || isFocused ? (
                    <EnhancedInput
                        type="textarea"
                        autoFocus
                        isFocused={isEditable}
                        textareaRows={3}
                        element="input"
                        value={description}
                        disabled={!isEditable}
                        theme={theme}
                        className={classnames(theme.formInput, {
                            [theme.isFocused]: isFocused
                        })}
                        onChange={onDescriptionChange}
                        onMouseEnter={disableMapEvents}
                        onMouseLeave={enableMapEvents}
                        onBlur={this.setInactive}
                    />
                ) : (
                    <Button
                        theme={theme}
                        label={descriptionLabel}
                        uppercase={false}
                        ripple={false}
                        color="dark-sky-blue"
                        className={theme.addDescriptionBtn}
                        type={ButtonTypes.Link}
                        onClick={this.setActive}
                    />
                )}
            </div>
        ) : null;
    };

    render() {
        const { mapMetadataSettings } = this.props;
        if (
            !mapMetadataSettings.showDescription &&
            !mapMetadataSettings.showPincodes &&
            !mapMetadataSettings.showPlaces &&
            !mapMetadataSettings.showRiders &&
            !mapMetadataSettings.showVehicles &&
            !mapMetadataSettings.showVehicleModels
        ) {
            return null;
        }
        return (
            <div className={theme.contentWrap}>
                {this.getLocalities()}
                {this.getPincodes()}
                {this.getRiders()}
                {this.getVehicles()}
                {this.getVehicleModels()}
                {this.getDescription()}
            </div>
        );
    }
}
const getNoOptionsMessageGetter = t => {
    return () => t('zoneMap.noFoldersFound');
};

const ZoneDetailsPopupFooter = ({
    t,
    isEditable,
    onDelete,
    enablePopupEditing,
    color,
    onStyleChange,
    folders,
    selectedFolder,
    onFolderChange,
    zoneType
}) => (
    <div className={theme.popupFooter}>
        <div className={theme.leftIcons}>
            <ButtonWithPopover
                className={theme.deleteBtn}
                theme={theme}
                buttonType={!isEditable ? ButtonTypes.Icon : undefined}
                icon="delete-bin"
                onClick={onDelete}
                ripple={isEditable ? false : true}
                semibold
                invertPopover
                hidePopoverOnClick
                popOverContent={
                    zoneType === ZoneMapGeometryTypeEnum.Polyline
                        ? t('zoneMap.deleteLine')
                        : t('zoneMap.deleteZone')
                }
                place={PopoverPlaceEnum.Below}
                triggerPopoverOn={PopoverTriggerEnum.Hover}
            />
            {!isEditable ? (
                <ButtonWithPopover
                    className={theme.editBtn}
                    theme={theme}
                    icon="edit-icon"
                    buttonType={ButtonTypes.Icon}
                    onClick={enablePopupEditing}
                    popOverContent={
                        zoneType === ZoneMapGeometryTypeEnum.Polyline
                            ? t('zoneMap.editLine')
                            : t('zoneMap.editZone')
                    }
                    place={PopoverPlaceEnum.Below}
                    triggerPopoverOn={PopoverTriggerEnum.Hover}
                    invertPopover
                />
            ) : null}
        </div>
        {isEditable ? (
            <div className={theme.rightIcons}>
                <ColourSelector
                    theme={theme}
                    color={color}
                    onChange={onStyleChange}
                    colors={Object.values(zoneMapColors)}
                    helpText={t('zoneMap.selectColour')}
                />

                <FolderSelector
                    theme={theme}
                    folders={folders}
                    selectedFolder={selectedFolder}
                    onChange={onFolderChange}
                    helpText={t('zoneMap.moveToFolder')}
                    noOptionsMessage={getNoOptionsMessageGetter(t)}
                />
            </div>
        ) : null}
    </div>
);

const ZoneDetailsPopup = ({
    t,
    isEditable,
    zone,
    selectedFolder,
    onStyleChange,
    folders,
    onClose,
    onNameChange,
    onLocalitiesChange,
    onPincodesChange,
    onFolderChange,
    onRouteIdChange,
    onDescriptionChange,
    onDelete,
    enablePopupEditing,
    enableMapEvents,
    disableMapEvents,
    pincodes,
    onRidersChange,
    availableRiders,
    zoneType,
    availableVehicles,
    availableVehicleModels,
    onVehiclesChange,
    onVehicleModelsChange,
    mapMetadataSettings
}) => {
    const {
        name,
        localities,
        pincodes: zonePincodes,
        users: riders,
        description,
        routeId,
        vehicles,
        vehicleModels
    } = zone;

    const { color } = zone.getStyles();
    return (
        <div
            className={classnames(theme.popup, {
                [theme.isEditable]: isEditable
            })}
        >
            <ZoneDetailsPopupHeader
                t={t}
                zoneType={zoneType}
                isEditable={isEditable}
                name={name}
                onNameChange={onNameChange}
                disableMapEvents={disableMapEvents}
                enableMapEvents={enableMapEvents}
                routeId={routeId}
                onRouteIdChange={onRouteIdChange}
                onClose={onClose}
            />

            <ZoneDetailsPopupContent
                t={t}
                isEditable={isEditable}
                localities={localities}
                onLocalitiesChange={onLocalitiesChange}
                disableMapEvents={disableMapEvents}
                enableMapEvents={enableMapEvents}
                riders={riders}
                onRidersChange={onRidersChange}
                zonePincodes={zonePincodes}
                onPincodesChange={onPincodesChange}
                pincodes={pincodes}
                description={description}
                onDescriptionChange={onDescriptionChange}
                availableRiders={availableRiders}
                zoneType={zoneType}
                availableVehicles={availableVehicles}
                availableVehicleModels={availableVehicleModels}
                onVehiclesChange={onVehiclesChange}
                onVehicleModelsChange={onVehicleModelsChange}
                vehicles={vehicles}
                vehicleModels={vehicleModels}
                mapMetadataSettings={mapMetadataSettings}
            />
            <ZoneDetailsPopupFooter
                t={t}
                zoneType={zoneType}
                isEditable={isEditable}
                onDelete={onDelete}
                enablePopupEditing={enablePopupEditing}
                color={color}
                zoneMapColors={zoneMapColors}
                onStyleChange={onStyleChange}
                folders={folders}
                selectedFolder={selectedFolder}
                onFolderChange={onFolderChange}
                mapMetadataSettings={mapMetadataSettings}
            />
        </div>
    );
};

export default translate('entityManagement')(ZoneDetailsPopup);
